import { PageHero } from "@/components/blocks/sections/PageHero";
import { Section } from "@/components/blocks/sections/Section";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import {
	BOOKING_URL,
	CONTACT_EMAIL,
	CONTACT_URL,
	FOUNDER_URL,
	QUESTOVERY_URL,
} from "@/lib/constants";
import type { ContactNeed } from "@/lib/contact";
import { useI18n } from "@/lib/i18n/use-i18n";
import { ContactForm } from "./ContactForm";

const linkClass =
	"border-b border-signal/60 pb-0.5 font-medium text-foreground transition-colors hover:border-signal";
const asideCard = "rounded-3xl border bg-card p-6 md:p-7";
const asideTitle = "mb-4 text-lg font-semibold tracking-tight";

export function ContactPage({ need }: { need?: ContactNeed }) {
	const { dict } = useI18n();
	const { capture } = useAnalytics();
	const page = dict.contact;
	const aside = page.aside;

	return (
		<>
			<PageHero
				label={page.hero.label}
				title={page.hero.title}
				emphasis={page.hero.emphasis}
				intro={page.hero.intro}
			/>
			<Section
				tone="stone"
				containerClassName="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_23rem] md:py-20"
			>
				<div className="relative rounded-[2rem] border bg-card p-6 shadow-sm md:p-10">
					<h2 className="mb-6 text-2xl font-semibold tracking-[-0.025em]">
						{page.form.title}
					</h2>
					<ContactForm initialNeed={need} />
				</div>

				<aside className="space-y-6">
					<div className={asideCard}>
						<h2 className={asideTitle}>{aside.next.title}</h2>
						<ol className="space-y-4 text-sm">
							{Object.entries(aside.next.steps).map(([key, step], index) => (
								<li key={key} className="flex gap-3">
									<span
										aria-hidden="true"
										className="flex size-6 shrink-0 items-center justify-center rounded-full border font-mono text-[10px] text-signal-ink"
									>
										{index + 1}
									</span>
									<div>
										<p className="font-medium">{step.title}</p>
										<p className="text-muted-foreground">{step.description}</p>
									</div>
								</li>
							))}
						</ol>
					</div>

					<div className={asideCard}>
						<h2 className={asideTitle}>{aside.direct.title}</h2>
						<div className="space-y-4 text-sm text-muted-foreground">
							<p>
								{aside.direct.booking}{" "}
								<a
									href={BOOKING_URL}
									target="_blank"
									rel="noopener"
									className={linkClass}
									onClick={() =>
										capture(ANALYTICS_EVENTS.ctaClick, {
											cta: "booking",
											placement: "contact_aside",
											href: BOOKING_URL,
										})
									}
								>
									{aside.direct.bookingLink}
								</a>
							</p>
							<p>
								{aside.direct.email}{" "}
								<a href={CONTACT_URL} className={linkClass}>
									{CONTACT_EMAIL}
								</a>
							</p>
						</div>
					</div>

					<div className={asideCard}>
						<h2 className={asideTitle}>{aside.orientation.title}</h2>
						<div className="space-y-4 text-sm text-muted-foreground">
							<p>
								{aside.orientation.staffing.text}{" "}
								<a
									href={FOUNDER_URL}
									target="_blank"
									rel="noopener"
									className={linkClass}
								>
									{aside.orientation.staffing.link}
								</a>
							</p>
							<p>
								{aside.orientation.product.text}{" "}
								<a
									href={QUESTOVERY_URL}
									target="_blank"
									rel="noopener"
									className={linkClass}
								>
									{aside.orientation.product.link}
								</a>
							</p>
						</div>
					</div>
				</aside>
			</Section>
		</>
	);
}
